import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/*
  SINS ACCORDION
  ----------------
  An intentional reveal.
  Opens only when allowed.
  Closes when silence is demanded.
*/

class SinsAccordion extends HTMLElement {
  // The truth container
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('SINS: No place to hide the truth.');
    }
    return details;
  }

  // The trigger — temptation
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) {
      throw new Error('SINS: No voice to confess.');
    }
    return summary;
  }

  // Desire denied on mobile
  get #denyOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  // Desire denied on desktop
  get #denyOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  // Silence triggered by escape
  get #escapeIntoSilence() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    // Decide what is revealed by default
    this.#setInitialState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.#handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleViewportChange, { signal });
  }

  disconnectedCallback() {
    // Everything fades when it ends
    this.#controller.abort();
  }

  // Temptation logic
  #handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if (
      (isMobile && this.#denyOnMobile) ||
      (isDesktop && this.#denyOnDesktop)
    ) {
      event.preventDefault();
    }
  };

  // Desire changes with the screen
  #handleViewportChange = () => {
    this.#setInitialState();
  };

  // What opens without asking
  #setInitialState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  // Escape returns everything to silence
  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#escapeIntoSilence) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('sins-accordion')) {
  customElements.define('sins-accordion', SinsAccordion);
}
